import { useEffect, type DependencyList } from 'react';
import {
  type CartPairing,
  type CartProduct,
  type PairingBundle,
  type CYOProduct,
} from '@hungryroot/rest';

export enum CartChangeType {
  AddRecipe = 'AddRecipe',
  AddProduct = 'AddProduct',
  RemoveRecipe = 'RemoveRecipe',
  RemoveProduct = 'RemoveProduct',
  RequestAddRecipe = 'RequestAddRecipe',
  RequestAddProduct = 'RequestAddProduct',
  RequestRemoveRecipe = 'RequestRemoveRecipe',
  RequestRemoveProduct = 'RequestRemoveProduct',
  AddCYOProduct = 'AddCYOProduct',
}

interface CartRequestAddRecipeEvent {
  type: CartChangeType.RequestAddRecipe;
}

interface CartRequestAddProductEvent {
  type: CartChangeType.RequestAddProduct;
}

interface CartRequestRemoveRecipeEvent {
  type: CartChangeType.RequestRemoveRecipe;
}

interface CartRequestRemoveProductEvent {
  type: CartChangeType.RequestRemoveProduct;
}

interface CartAddPairingEvent {
  type: CartChangeType.AddRecipe;
  bundle: PairingBundle | null;
  pairing: CartPairing;
}

interface CartAddProductEvent {
  type: CartChangeType.AddProduct;
  product: CartProduct;
}

interface CartRemovePairingEvent {
  type: CartChangeType.RemoveRecipe;
  bundle: PairingBundle | null;
}

interface CartRemoveProductEvent {
  type: CartChangeType.RemoveProduct;
  product: CartProduct | null;
}

interface CartAddCYOProductEvent {
  type: CartChangeType.AddCYOProduct;
  product: CYOProduct;
}

export type CartChangeEvent =
  | CartAddPairingEvent
  | CartAddProductEvent
  | CartRemovePairingEvent
  | CartRemoveProductEvent
  | CartAddCYOProductEvent;

type CartRequestChangeEvent =
  | CartRequestAddRecipeEvent
  | CartRequestAddProductEvent
  | CartRequestRemoveRecipeEvent
  | CartRequestRemoveProductEvent;

type OnSaveHandler = () => void;
type OnChangeHandler = (data: CartChangeEvent) => void;
type OnRequestChangeHandler = (data: CartRequestChangeEvent) => void;

export const cartOnChangeHandlers: OnChangeHandler[] = [];
const cartRequestChangeHandlers: OnRequestChangeHandler[] = [];
const cartRequestSaveHandlers: OnSaveHandler[] = [];

export const listenToCartChangeEvents = (handler: OnChangeHandler) => {
  cartOnChangeHandlers.push(handler);
  return () => {
    const index = cartOnChangeHandlers.indexOf(handler);
    cartOnChangeHandlers.splice(index, 1);
  };
};

const listenToRequestChangeEvents = (handler: OnRequestChangeHandler) => {
  cartRequestChangeHandlers.push(handler);
  return () => {
    const index = cartRequestChangeHandlers.indexOf(handler);
    cartRequestChangeHandlers.splice(index, 1);
  };
};

const listenToRequestSaveEvents = (handler: OnSaveHandler) => {
  cartRequestSaveHandlers.push(handler);
  return () => {
    const index = cartRequestSaveHandlers.indexOf(handler);
    cartRequestSaveHandlers.splice(index, 1);
  };
};

export function executeRequestSaveHandlers() {
  executeHandlers(cartRequestSaveHandlers, undefined);
}

export function executeRequestChangeHandlers(payload: CartRequestChangeEvent) {
  executeHandlers(cartRequestChangeHandlers, payload);
}

export function executeChangeHandlers(payload: CartChangeEvent) {
  executeHandlers(cartOnChangeHandlers, payload);
}

function executeHandlers<A, T extends (a: A) => void>(handlers: T[], data: A) {
  [...handlers].forEach((handler) => {
    handler(data);
  });
}

interface UseCartEventListeners {
  onChange?: OnChangeHandler;
  onRequestChange?: OnRequestChangeHandler;
  onRequestSave?: OnSaveHandler;
}

export function useCartEvents(
  params: UseCartEventListeners,
  deps?: DependencyList
) {
  const depList = deps === undefined ? [] : deps;
  const { onChange, onRequestChange, onRequestSave } = params;
  useEffect(() => {
    const unsubs: Array<() => void> = [];
    if (onChange !== undefined) {
      unsubs.push(listenToCartChangeEvents(onChange));
    }

    if (onRequestSave !== undefined) {
      unsubs.push(listenToRequestSaveEvents(onRequestSave));
    }

    if (onRequestChange !== undefined) {
      unsubs.push(listenToRequestChangeEvents(onRequestChange));
    }
    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [onChange, onRequestChange, ...depList]);
}
